import Cytoscape from 'cytoscape';
import COSEBilkent from 'cytoscape-cose-bilkent';
import tidytree from "cytoscape-tidytree";
import cola from 'cytoscape-cola';
import React, { useContext, useEffect, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import { ProjectContext } from '../../contexts/ProjectContext';
import '../../assets/cytoscape-context-menus.css';
import jquery from 'jquery';
import edgehandles from 'cytoscape-edgehandles';
import elk from 'cytoscape-elk';
import { ArrowType } from '../../types';

Cytoscape.use(edgehandles);
Cytoscape.use(COSEBilkent);
//Cytoscape.use(tidytree);
Cytoscape.use(cola);
Cytoscape.use(elk);
Cytoscape.use(jquery);

// Darken a hex/rgb(a) color by the given ratio (0..1)
function darkenColor(color, ratio = 0.2) {
    if (!color) return color;
    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));

    // Handle hex #RGB or #RRGGBB
    const hexMatch = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color);
    if (hexMatch) {
        let hex = hexMatch[1];
        if (hex.length === 3) {
            hex = hex.split('').map(ch => ch + ch).join('');
        }
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const dr = clamp(r * (1 - ratio));
        const dg = clamp(g * (1 - ratio));
        const db = clamp(b * (1 - ratio));
        const toHex = (n) => n.toString(16).padStart(2, '0');
        return `#${toHex(dr)}${toHex(dg)}${toHex(db)}`;
    }

    // Handle rgb/rgba
    const rgbMatch = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)$/i.exec(color);
    if (rgbMatch) {
        const r = clamp(parseInt(rgbMatch[1], 10));
        const g = clamp(parseInt(rgbMatch[2], 10));
        const b = clamp(parseInt(rgbMatch[3], 10));
        const a = rgbMatch[4] !== undefined ? Math.max(0, Math.min(1, parseFloat(rgbMatch[4]))) : undefined;
        const dr = clamp(r * (1 - ratio));
        const dg = clamp(g * (1 - ratio));
        const db = clamp(b * (1 - ratio));
        return a === undefined ? `rgb(${dr}, ${dg}, ${db})` : `rgba(${dr}, ${dg}, ${db}, ${a})`;
    }

    // Fallback: return original if format unrecognized
    return color;
}

export const ProjectCanvas = forwardRef((props, ref) => {
    const { projectData, elements, deleteElementDialog, SelectItem, setShowNewNodeDialog, addConnection } = useContext(ProjectContext);

    // Fix the issue of duplicate edges being shown
    // Reason: cytoscape-edgehandles automatically adds the edge to the graph when the gesture completes,
    // but you are also saving to firebase and then updating elements (which React gives to the CytoscapeComponent).
    // So Cytoscape ends up with two edges: one added directly by edgehandles, one via React/ELEMENTS.

    // Solution: Prevent edgehandles from automatically adding an edge by setting "complete" option
    // and handle edge creation only via addConnection(), which updates Firebase and drives the React state (elements).

    // Use a ref to always access up-to-date Cytoscape handle
    const myCyRef = useRef();
    // Ref to track previous elements structure to prevent unnecessary layout refreshes
    const prevElementsStructureRef = useRef(null);

    // Helper function to create a structural signature of elements (for comparison)
    const getElementsSignature = (els) => {
        if (!els || els.length === 0) return '';
        // Create a signature based on element IDs and edge connections
        const nodeIds = els
            .filter(el => el.group === 'nodes')
            .map(el => el.data?.id)
            .sort()
            .join(',');
        const edgeConnections = els
            .filter(el => el.group === 'edges')
            .map(el => `${el.data?.source || ''}-${el.data?.target || ''}`)
            .sort()
            .join(',');
        return `${els.length}:${nodeIds}:${edgeConnections}`;
    };

    // If elements are too close with the "disco" algorithm, adjust additional ELK spacing options.
    // Try using a combination of 'spacing.nodeNode', 'spacing.edgeEdge', and 'spacing.nodeNodeBetweenLayers'
    // These are supported by 'disco', even if 'spacing.componentComponent' is not.
    // You can dynamically provide different options for disco:

    const getElkOptions = (layoutAlg) => {
        if (layoutAlg === 'disco') {
            return {
                algorithm: "disco",
                // Increase spacing between nodes and edges for disco
                'spacing.nodeNode': 100,
                'spacing.edgeEdge': 80,
                'spacing.nodeNodeBetweenLayers': 80,
                // You could also try 'elk.spacing.baseValue' for all-round spacing:
                'elk.spacing.baseValue': 100,
                componentLayoutAlgorithm: 'stress',
                // Remove componentComponent for disco, as it's ignored/unhelpful
            };
        }
        // Default: use original options (for other algorithms)
        return {
            algorithm: layoutAlg,
            separateConnectedComponents: false,
            componentLayoutAlgorithm: 'stress',
            'spacing.componentComponent': 80,
        };
    };

    // Memoize layout object to prevent unnecessary re-renders and layout recalculations
    const layout = useMemo(() => ({ 
        name: projectData.layout === "layered" || projectData.layout === "mrtree" ? "elk" : projectData.layout,
        fit: true,
        padding: 30, // Padding on fit
        ranker: 'longest-path',
        animate: true,
        animationDuration: 500,
        animationEasing: 'ease-in-out-cubic',
        nodeRepulsion: 4500,      // Increase from default ~4500 for stronger push between nodes
        idealEdgeLength: ((projectData?.elementsize ?? 25) * (projectData?.elementspacing ?? 3)), // Defaults to 50 if elementsize is not set
        elk: {
            ...getElkOptions(projectData.layout)
        },
    }), [projectData?.layout, projectData?.elementsize, projectData?.elementspacing]);

    let edgehandles_defaults = {
        canConnect: function (sourceNode, targetNode) {
            return !sourceNode.same(targetNode);
        },
        edgeParams: function (sourceNode, targetNode) {
            // This is no-op, we don't let edgehandles add the edge.
            return {};
        },
        hoverDelay: 150,
        snap: true,
        snapThreshold: 50,
        snapFrequency: 15,
        noEdgeEventsInDraw: true,
        disableBrowserGestures: true,
    };

    useEffect(() => {
        if (!myCyRef.current) return;

        myCyRef.current.on('tap', function (event) {
            var evtTarget = event.target;
            if (evtTarget === myCyRef.current) {
                SelectItem('x');
            } else {
                SelectItem(event.target.data().id);
            }
        });

        // Degree update handler: call this whenever cytoscape content might be new/changed
        const updateNodeDegrees = () => {
            myCyRef.current.nodes().forEach(node => {
                node.data('degree', node.degree());
            });
        };

        // Update degrees on add/remove of nodes/edges
        myCyRef.current.on('add remove', 'node, edge', () => {
            updateNodeDegrees();
        });

        // Also update degrees after initial cy data set/render
        myCyRef.current.ready(function() {
            updateNodeDegrees();
        });

        myCyRef.current.on('ehcomplete', (event, sourceNode, targetNode, addedEdge) => {
            if (addedEdge) {
                addedEdge.remove();
            }
            addConnection(sourceNode.data().id, targetNode.data().id, "PARENT", true);
        });

        // Remove original ehcomplete handler! Instead, we now use the 'complete' handler in edgehandles options above.

        myCyRef.current.edgehandles(edgehandles_defaults);
        myCyRef.current.minZoom(0.2);
        myCyRef.current.maxZoom(4);

        setLayout();
    // eslint-disable-next-line
    }, []);

    useEffect(() => {
        // Only refresh layout if elements changed structurally (not just selection)
        const currentSignature = getElementsSignature(elements);
        const prevSignature = prevElementsStructureRef.current;
        
        if (currentSignature !== prevSignature) {
            prevElementsStructureRef.current = currentSignature;
            setLayout();
        }
    // eslint-disable-next-line
    }, [elements]);

    // Refresh Cytoscape layout using current layout parameters and options, without redefining:
    const setLayout = () => {
        // Only proceed if cy and projectData are safely defined
        if (!myCyRef.current || !projectData) return;
        const cy = myCyRef.current;

        // Safeguard layout name (avoid null/undefined/invalid layout)
        let layoutName = "cose-bilkent"; // default fallback
        if (typeof projectData.layout === "string") {
            if (projectData.layout === "layered" || projectData.layout === "mrtree") {
                layoutName = "elk";
            } else {
                layoutName = projectData.layout;
            }
        }

        // Safeguard element size/spacing, use fallback if null/undefined
        const elementSize = typeof projectData.elementsize === "number"
            ? projectData.elementsize
            : 25;
        const elementSpacing = typeof projectData.elementspacing === "number"
            ? projectData.elementspacing
            : 3;

        let elkOptions = {};
        try {
            elkOptions = typeof getElkOptions === "function"
                ? getElkOptions(projectData.layout)
                : {};
        } catch (e) {
            elkOptions = {};
        }

        const layoutOptions = {
            name: layoutName,
            fit: true,
            padding: 30,
            ranker: 'longest-path',
            animate: true,
            animationDuration: 500,
            animationEasing: 'ease-in-out-cubic',
            nodeRepulsion: 4500,
            idealEdgeLength: (elementSize * elementSpacing),
            elk: {
                ...elkOptions
            },
        };

        try {
            cy.layout(layoutOptions).run();
        } catch (e) {
            // fallback in case of error
            console.error("setLayout failed: " + e);
        }
    }

    useImperativeHandle(ref, () => ({
        ConnectionStart() {
            if (myCyRef.current) {
                var eh = myCyRef.current.edgehandles();
                eh.start(myCyRef.current.$('node:selected'));
            }
        },
        setLayout,
        SelectItemOnCanvas(id) {
            if (myCyRef.current) {
                var typeIds = myCyRef.current.elements(':selected');
                typeIds.forEach((selectedId, index) => {
                    myCyRef.current.getElementById(selectedId.data().id).unselect();
                });
                myCyRef.current.getElementById(id).select();
            }
        },
        FitToSelected() {
            if (myCyRef.current) {
                myCyRef.current.animate({
                    fit: { eles: myCyRef.current.elements(':selected') },
                    duration: 500
                });
            }
        },
        Fit() {
            if (myCyRef.current) {
                myCyRef.current.animate({
                    fit: {},
                    duration: 500
                });
            }
        },
        ExportToPng() {
            if (myCyRef.current) {
                var options = {
                    full: true,
                    maxWidth: 3000
                };

                var png64 = myCyRef.current.png(options);
                var a = document.createElement("a");
                a.href = png64;
                a.download = "Branchout_export.png";
                a.click();
            }
        }
    }));

    const NodeCanvasStyle = [
        {
            "selector": "node",
            "style": {
                "label": (ele) => {if (ele.data()?.textshowlabel === undefined) return (projectData?.themetextshowlabel ? ele.data()?.name : ""); else return (ele.data()?.textshowlabel ? ele.data()?.name : "")},
                "shape": (ele) => {if (!ele.data()?.nodeshape) return projectData?.defaultshapetype; else return ele.data()?.nodeshape},
                "width": (ele) => ( projectData?.dynamicsize ? (ele.data('degree') * 10) + projectData?.elementsize : projectData?.elementsize).toString(),
                "height": (ele) => ( projectData?.dynamicsize ? (ele.data('degree') * 10) + projectData?.elementsize : projectData?.elementsize).toString(),
                "font-size": (ele) => {if (!ele.data()?.textsize) return projectData?.themetextsize + "px"; else return ele.data()?.textsize + "px"},
                "text-valign": "center",
                "text-halign": "center",
                "background-color": (ele) => {if (!ele.data()?.elementcolor) return projectData?.themeelementcolor; else return ele.data()?.elementcolor},
                "text-outline-color": (ele) => {if (!ele.data()?.textbordercolor) return projectData?.themetextbordercolor; else return ele.data()?.textbordercolor},
                "text-outline-width": (ele) => {if (!ele.data()?.textborderwidth) return projectData?.themetextborderwidth + "px"; else return ele.data()?.textborderwidth + "px"},
                "border-width": (ele) => {if (!ele.data()?.borderwidth) return projectData?.themeborderwidth + "px"; else return ele.data()?.borderwidth + "px"},
                "border-color": (ele) => {if (!ele.data()?.bordercolor) return projectData?.themebordercolor; else return ele.data()?.bordercolor},
                "color": (ele) => {if (!ele.data()?.textcolor) return projectData?.themetextcolor; else return ele.data()?.textcolor},
                "overlay-padding": "6px",
                "background-fit":"cover",
                "background-image": (ele) => {
                    let img = ele.data()?.image || ele.data()?.backgroundImage;
                    if (!img) return undefined;
                    // Cytoscape background-image requires CSS url() syntax for all image URLs
                    if (!/^url\(/.test(img)) {
                        return `url(${img})`;
                    }
                    return img;
                },
                "font-family": "Roboto, Georgia, serif",
                //"font-family": "Recursive",
            }
        },
        {
            "selector": "node:selected",
            "style": {
                "background-color": (ele) => {
                    const base = ele.data()?.elementcolor ? ele.data()?.elementcolor : projectData?.themeelementcolor;
                    return darkenColor(base, 0.3);
                },
            }
        },
        {
            "selector": "node[type]",
            "style": {
                "label": "data(type)"
            }
        },
        {
            "selector": "edge",
            "style": {
                "curve-style": "bezier",
            "label": (ele) => {if (ele.data()?.edgeshowlabel === undefined) return (projectData?.themeedgeshowlabel ? ele.data()?.name : ""); else return (ele.data()?.edgeshowlabel ? ele.data()?.name : "")},
                "width": "2",
                "line-color": (ele) => {if (!ele.data()?.edgecolor) return projectData?.themeedgecolor; else return ele.data()?.edgecolor},
                "line-style": "data(edgetype)",
                "target-arrow-shape":  (ele) => {if (!ele.data()?.edgearrows) {
                    return "none";
                } else {
                    if (ele.data()?.edgearrows === ArrowType[0].value) return "none";
                    if (ele.data()?.edgearrows === ArrowType[1].value) return "triangle";
                    if (ele.data()?.edgearrows === ArrowType[2].value) return "none";
                    if (ele.data()?.edgearrows === ArrowType[3].value) return "triangle";
                    return "none"}
                }, //"triangle",
                "source-arrow-shape": (ele) => {if (!ele.data()?.edgearrows) {
                    return "none";
                } else {
                    if (ele.data()?.edgearrows === ArrowType[0].value) return "none";
                    if (ele.data()?.edgearrows === ArrowType[1].value) return "none";
                    if (ele.data()?.edgearrows === ArrowType[2].value) return "triangle";
                    if (ele.data()?.edgearrows === ArrowType[3].value) return "triangle";
                    return "none"}
                }, //"triangle",
                "target-arrow-color": (ele) => {if (!ele.data()?.edgecolor) return projectData?.themeedgecolor; else return ele.data()?.edgecolor},
                "source-arrow-color": (ele) => {if (!ele.data()?.edgecolor) return projectData?.themeedgecolor; else return ele.data()?.edgecolor},
            }
        },
        {
            "selector": "edge:selected",
            "style": {
                "line-color": (ele) => {
                    const base = ele.data()?.edgecolor ? ele.data()?.edgecolor : projectData?.themeedgecolor;
                    return darkenColor(base, 0.4);
                },
                "target-arrow-color": (ele) => {
                    const base = ele.data()?.edgecolor ? ele.data()?.edgecolor : projectData?.themeedgecolor;
                    return darkenColor(base, 0.4);
                },
                "source-arrow-color": (ele) => {
                    const base = ele.data()?.edgecolor ? ele.data()?.edgecolor : projectData?.themeedgecolor;
                    return darkenColor(base, 0.4);
                },
            }
        },
        {
            "selector": "core",
            "style": {
              'active-bg-color': 'green',
                'active-bg-opacity': 0.2,
                'selection-box-color': 'blue',
                'outside-texture-bg-color': 'red',
            },
        }
    ]

    return (
        <>
            <CytoscapeComponent
                id="cy"
                container="cy"
                stylesheet={NodeCanvasStyle}
                cy={cy => {
                    myCyRef.current = cy;
                }}
                elements={props.elements}
                wheelSensitivity="0.1"
                pixelRatio="auto"
                zoomingEnabled
                style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, background: projectData?.backgroundcolor }} />
        </>
    );
});
